import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiRefreshCw, FiSearch, FiUnlock } from "react-icons/fi";
import { toast } from "react-toastify";
import PaginationControls from "../../PaginationControls";
import UnlockSubmissionModal from "./UnlockSubmissionModal";
import {
  fetchApprovedTimesheetSubmissions,
  fetchTimesheetDashboardUsers,
  unlockTimesheetSubmission,
} from "../../../services/timesheetApi";
import { mapUnlockFieldErrors } from "../../../utils/timesheetApprovalAccess";
import { formatHours, getEntityId, normalizeDateKey } from "../../../utils/timesheetUtils";

const PAGE_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_OPTIONS = [
  { value: "approved", label: "อนุมัติแล้ว" },
  { value: "unlocked", label: "เคยปลดล็อก" },
];

const formatPeriod = (item) => {
  const start = normalizeDateKey(item?.periodStart);
  const end = normalizeDateKey(item?.periodEnd);
  return start && end ? `${start} ถึง ${end}` : "-";
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("th-TH") : "-");

const getDisplayName = (user = {}) => user.displayName || user.username || "-";

const getApproverNames = (steps = []) => {
  const names = steps
    .filter((step) => String(step?.status || "").toLowerCase() === "approved")
    .map((step) => step.approverUsername)
    .filter(Boolean);
  return names.length ? names : ["-"];
};

const ApprovedSubmissionsTab = () => {
  const [statusMode, setStatusMode] = useState("approved");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockErrors, setUnlockErrors] = useState({});
  const requestSequenceRef = useRef(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // ส่งช่วงวันที่เฉพาะเมื่อเลือกครบคู่
  const hasDateRange = Boolean(from && to);
  const appliedFrom = hasDateRange ? from : "";
  const appliedTo = hasDateRange ? to : "";

  // เปลี่ยนตัวกรองแล้วกลับไปหน้าแรก
  const withPageReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  useEffect(() => {
    let active = true;
    fetchTimesheetDashboardUsers()
      .then((response) => {
        if (active) setUsers(response.canViewDashboard ? response.data : []);
      })
      .catch(() => {
        if (active) setUsers([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const loadItems = useCallback(async () => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;

    try {
      setLoading(true);
      setError("");
      const response = await fetchApprovedTimesheetSubmissions({
        status: statusMode,
        userId,
        from: appliedFrom,
        to: appliedTo,
        search,
        page,
        limit: PAGE_LIMIT,
      });
      if (requestSequenceRef.current !== requestSequence) return;
      setItems(response.data);
      setPagination(response.pagination);
    } catch (requestError) {
      if (requestSequenceRef.current !== requestSequence) return;
      const status = requestError?.response?.status;
      setItems([]);
      setError(
        status === 403
          ? "คุณไม่มีสิทธิ์ดูรายการ Timesheet ที่อนุมัติแล้ว"
          : requestError?.response?.data?.message || "ไม่สามารถโหลดรายการ Timesheet ที่อนุมัติแล้วได้"
      );
    } finally {
      if (requestSequenceRef.current === requestSequence) setLoading(false);
    }
  }, [appliedFrom, appliedTo, page, search, statusMode, userId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const closeUnlockModal = useCallback(() => {
    if (unlocking) return;
    setUnlockTarget(null);
    setUnlockErrors({});
  }, [unlocking]);

  const handleUnlock = async ({ reason, reopenUntil }) => {
    if (!unlockTarget || unlocking) return;
    const id = unlockTarget._id || unlockTarget.id;

    try {
      setUnlocking(true);
      setUnlockErrors({});
      const result = await unlockTimesheetSubmission(id, { reason, reopenUntil });
      const reopenedUntil = result?.reopenUntil ?? result?.data?.reopenUntil ?? null;
      toast.success(
        reopenedUntil
          ? `ปลดล็อก Timesheet แล้ว เปิดให้แก้ไขถึงวันที่ ${normalizeDateKey(reopenedUntil)}`
          : "ปลดล็อก Timesheet แล้ว"
      );
      setUnlockTarget(null);
      loadItems();
    } catch (requestError) {
      const status = requestError?.response?.status;
      const responseData = requestError?.response?.data;

      if (status === 400) {
        setUnlockErrors(mapUnlockFieldErrors(responseData));
      } else if (status === 409) {
        toast.warning("รายการนี้ถูกปลดล็อกหรือเปลี่ยนสถานะไปแล้ว");
        setUnlockTarget(null);
        loadItems();
      } else if (status === 403) {
        toast.error("คุณไม่มีสิทธิ์ปลดล็อก Timesheet นี้");
      } else {
        toast.error(responseData?.message || "ไม่สามารถปลดล็อก Timesheet ได้");
      }
    } finally {
      setUnlocking(false);
    }
  };

  const isUnlockedMode = statusMode === "unlocked";
  const totalPages = Math.max(Number(pagination?.totalPages) || 1, 1);
  const inputClass = "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-indigo-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

  return (
    <>
      <div className="mt-6 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex w-full rounded-2xl border border-gray-200 p-1 dark:border-gray-700 sm:w-auto">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => withPageReset(setStatusMode)(option.value)}
                className={`flex-1 rounded-xl px-4 py-2 text-sm font-medium transition sm:flex-none ${statusMode === option.value ? "bg-indigo-500 text-white shadow-sm" : "text-gray-600 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-indigo-300"}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={loadItems} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"><FiRefreshCw className={loading ? "animate-spin" : ""} /> รีเฟรช</button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <label className="relative">
            <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">ค้นหาชื่อ/อีเมล</span>
            <FiSearch className="pointer-events-none absolute left-3 top-[43px] text-gray-400" />
            <input type="text" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="ค้นหาพนักงาน" className={`${inputClass} pl-10`} />
          </label>
          <label>
            <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">ตั้งแต่วันที่</span>
            <input type="date" value={from} max={to || undefined} onChange={(event) => withPageReset(setFrom)(event.target.value)} className={inputClass} />
          </label>
          <label>
            <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">ถึงวันที่</span>
            <input type="date" value={to} min={from || undefined} onChange={(event) => withPageReset(setTo)(event.target.value)} className={inputClass} />
          </label>
          {users.length > 0 && (
            <label>
              <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">พนักงาน</span>
              <select value={userId} onChange={(event) => withPageReset(setUserId)(event.target.value)} className={inputClass}>
                <option value="">ทั้งหมด</option>
                {users.map((user) => {
                  const id = getEntityId(user._id || user.userId || user.id);
                  return <option key={id} value={id}>{getDisplayName(user)}</option>;
                })}
              </select>
            </label>
          )}
        </div>
        {(from || to) && !hasDateRange && (
          <p className="mt-2 text-xs text-amber-600 dark:text-amber-300">กรุณาเลือกวันที่ให้ครบทั้งสองช่องเพื่อกรองตามช่วงวันที่</p>
        )}
      </div>

      <div className="mt-6 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? <p className="px-6 py-14 text-center text-sm text-gray-500 dark:text-gray-400">กำลังโหลดรายการ...</p> : error ? <p className="px-6 py-12 text-center text-sm font-medium text-red-600 dark:text-red-300">{error}</p> : !items.length ? <p className="px-6 py-14 text-center text-sm text-gray-500 dark:text-gray-400">{isUnlockedMode ? "ไม่มี Timesheet ที่เคยปลดล็อก" : "ไม่มี Timesheet ที่อนุมัติแล้ว"}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/60">
                <tr>
                  <th className="px-4 py-3 text-left">พนักงาน</th>
                  <th className="px-4 py-3 text-left">ช่วงสัปดาห์</th>
                  <th className="px-4 py-3 text-right">ชั่วโมงรวม</th>
                  <th className="px-4 py-3 text-left">วันที่อนุมัติ</th>
                  <th className="px-4 py-3 text-left">ผู้อนุมัติ</th>
                  {isUnlockedMode && <th className="px-4 py-3 text-left">การปลดล็อก</th>}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="[&>tr:nth-child(even)]:bg-slate-50/80 dark:[&>tr:nth-child(even)]:bg-gray-900/35">
                {items.map((item) => {
                  const id = item._id || item.id;
                  const user = item.user || {};
                  return (
                    <tr key={id} className="border-t border-gray-100 align-top dark:border-gray-700/80">
                      <td className="max-w-[240px] px-4 py-4">
                        <p className="break-all font-medium text-gray-900 dark:text-gray-100">{getDisplayName(user)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user.department || "-"}</p>
                      </td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300">{formatPeriod(item)}</td>
                      <td className="px-4 py-4 text-right text-gray-700 dark:text-gray-200">{formatHours(item.totalHours)}</td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300">{formatDateTime(item.approvedAt)}</td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300">{getApproverNames(item.approvalSteps).map((name, index) => <p key={`${name}-${index}`} className="break-all">{name}</p>)}</td>
                      {isUnlockedMode && (
                        <td className="max-w-[260px] px-4 py-4 text-gray-600 dark:text-gray-300">
                          <p className="font-medium text-gray-800 dark:text-gray-100">{item.unlockedBy?.displayName || item.unlockedBy?.username || "-"}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(item.unlockedAt)}</p>
                          {item.unlockReason && <p className="mt-1 break-words text-xs">เหตุผล: {item.unlockReason}</p>}
                        </td>
                      )}
                      <td className="px-4 py-4 text-right">
                        {item.canUnlock === true && (
                          <button type="button" onClick={() => { setUnlockErrors({}); setUnlockTarget(item); }} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-xs font-medium text-white hover:bg-amber-600"><FiUnlock /> ปลดล็อก</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && totalPages > 1 && (
        <PaginationControls currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      <UnlockSubmissionModal
        submission={unlockTarget}
        employeeName={getDisplayName(unlockTarget?.user)}
        periodLabel={formatPeriod(unlockTarget)}
        submitting={unlocking}
        serverErrors={unlockErrors}
        onClose={closeUnlockModal}
        onConfirm={handleUnlock}
      />
    </>
  );
};

export default ApprovedSubmissionsTab;
